import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useLoaderData } from "react-router-dom";
import { PatchTownValues, PatchTownSchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationMultiInput } from "../components/RelationMultiInput";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { SuccessResponse, fetch } from "@/src/lib/fetch";
import { Country, Region, Town } from "../model";
import { Button } from "@/src/components/ui/button";
import { extractFromMultiName } from "../utils";
import { Yacht } from "../../Yachts/model";
import { YachtsSection } from "../components/YachtsSection";
import { FormCheckbox } from "@/src/components/ui/form-checkbox";
import { FormErrors } from "../components/FormErrors";

export async function townLoader({ params }: { params: { townId: string } }) {
  const response = await fetch<SuccessResponse<Town>>(`/api/locations/towns/${params.townId}`);
  return { defaultValues: response.data, townId: params.townId };
}

export function EditTown() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaultValues, townId } = useLoaderData() as {
    defaultValues: Partial<PatchTownValues>;
    townId: string;
  };

  const methods = useForm<PatchTownValues>({
    resolver: zodResolver(PatchTownSchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const loadRegions = async (q: string) => {
    const data = await fetch<{ data: Region[] }>(
      `/api/locations/regions?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const loadCountries = async (q: string) => {
    const data = await fetch<{ data: Country[] }>(
      `/api/locations/countries?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const loadYachts = async () => {
    const data = await fetch<{ data: Yacht[] }>(`/api/locations/towns/${townId}/yachts`);
    return data.data;
  };

  const onSubmit = async (data: PatchTownValues) => {
    try {
      await fetch(`/api/locations/towns/${townId}`, "PATCH", data);
      toast({ title: "Success", description: "Town updated successfully" });
      navigate(`/locations/towns/${townId}`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const onDelete = async () => {
    if (window.confirm("Are you sure you want to delete this town?")) {
      try {
        await fetch(`/api/locations/towns/${townId}`, "DELETE");
        toast({ title: "Success", description: "Town deleted successfully" });
        navigate("/locations/towns");
      } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl shadow-md bg-white p-4">
        <h2 className="text-lg font-semibold">Edit Town - {extractFromMultiName(defaultValues.names)}</h2>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
            <div>
              <Label>Names</Label>
              <Textarea {...register("names")} placeholder="Enter names, one per line" />
              {errors.names && <p className="text-red-500">{errors.names.message}</p>}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea {...register("notes")} placeholder="Notes" />
            </div>

            <RelationMultiInput name="regions" label="Regions" loadOptions={loadRegions} />

            <RelationSingleInput name="country" label="Country" loadOptions={loadCountries} />

            <FormErrors errors={errors} />

            <Button type="submit">Save</Button>
            <Button variant="destructive" type="button" onClick={onDelete} className="ml-4">
              Delete
            </Button>
          </form>
        </FormProvider>
      </div>
      <YachtsSection loadYachts={loadYachts} />
    </div>
  );
}
